/* eslint-disable max-lines */
import type { UserTypes } from "@hub/common-libs";
import toQueryString from "@utils/queryString";
import environment from "environment";
import { default as i18n } from "i18next";
import { User } from "src/interfaces/user";
import { GetState, SetState } from "zustand";

import {
  type FilterValues,
  type UserManagementServiceResponse,
  UserStatus,
} from "@pages/UserManagement/UserManagement.types";
import { BaseService } from "@services/BaseService";

import { CombinedStore, UserManagementStore } from "../useCombinedStore.types";

export const userManagementStore = (
  set: SetState<CombinedStore>,
  get: GetState<CombinedStore>
): UserManagementStore => ({
  users: [],
  user: {
    email: "",
    role: { roleId: "", roleName: "" },
    organisation: { id: "", orgName: "" },
    status: UserStatus.ACTIVE,
  },
  Customers: {},
  paginationKey: "",
  userValidationSchema: [],
  countriesData: [],
  countiresDropdownList: [],
  selectedUsers: [],
  getUsers: {
    loading: false,
    fetched: false,
    error: "",
    actions: {},
    call: (
      searchItem?: string,
      selectAll?: boolean,
      filterData?: FilterValues
    ) => {
      // Clear errors & set the loading state.
      set((state) => {
        state.getUsers.loading = true;
        state.getUsers.error = "";
      });

      const { paginationKey } = get();

      const isSearching = !!searchItem;

      const baseUrl = isSearching
        ? environment.api.users + "/search"
        : environment.api.users;

      const queryParams = {
        paginationKey,
        input: searchItem?.toLocaleLowerCase(),
        orgId: filterData?.orgId,
        status: filterData?.status,
        role: filterData?.role,
      };

      const queryString = toQueryString(queryParams);

      return new Promise<User[]>((resolve, reject) => {
        BaseService.get(baseUrl + `?${queryString}`)
          .then((res) => {
            const { data } = res.data;
            const { users, actions, paginationKey } = data;

            let usersWithRoles;

            if (users) {
              usersWithRoles = users.map((user: User) => {
                return {
                  ...user,
                  roleId: user.role === undefined ? "NA" : user.role.roleId,
                  roleName: user.role === undefined ? "NA" : user.role.roleName,
                };
              });

              set((state) => {
                state.getUsers.fetched = true;
                state.paginationKey = paginationKey || "";
                if (selectAll)
                  state.selectedUsers = [...state.selectedUsers, ...users];
              });
            } else {
              set((state) => {
                state.getUsers.error = i18n.t("shared:users:no_users");
              });
            }

            // Add actions to the 'actions' state.
            if (actions) {
              set((state) => {
                state.getUsers.actions = actions;
              });
            }

            resolve(usersWithRoles);
          })
          .catch(() => {
            set((state) => {
              state.getUsers.error = i18n.t(
                "shared:users:failed_to_load_users"
              );
            });
            reject();
          })
          .finally(() => {
            set((state) => {
              state.getUsers.loading = false;
              state.getUsers.fetched = true;
            });
          });
      });
    },
  },
  fetchUserValidationSchema: {
    loading: false,
    error: "",
    call: () => {
      set((state) => {
        state.fetchUserValidationSchema.loading = true;
      });
      return new Promise<void>((resolve, reject) => {
        BaseService.get(environment.api.userSchema)
          .then(function (res) {
            set((state) => {
              state.userValidationSchema = res.data;
              state.countiresDropdownList = res.data[4].data.value;
            });
            resolve();
          })
          .catch(() => {
            set((state) => {
              state.fetchUserValidationSchema.error = i18n.t(
                "shared:users:api_failure"
              );
            });
            reject();
          })
          .finally(() => {
            set((state) => {
              state.fetchUserValidationSchema.loading = false;
            });
          });
      });
    },
  },
  addUser: {
    loading: false,
    call: (user: UserTypes.CreateUserData) => {
      const roleIdString = user.roleId;
      user.roleId = roleIdString?.split("$")[0];
      set((state) => {
        state.addUser.loading = true;
      });
      return new Promise<UserTypes.CreateServiceResponse>((resolve, reject) => {
        BaseService.post(environment.api.users, user)
          .then(function (res) {
            set((state) => {
              get().getUsers.call();
              state.users.sort((a: User, b: User) =>
                a?.email > b?.email ? 1 : b?.email > a?.email ? -1 : 0
              );
            });
            resolve(res.data);
          })
          .catch(function (error) {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.addUser.loading = false;
            });
          });
      });
    },
  },
  updateUser: {
    loading: false,
    call: (user: User) => {
      const { email, userId } = user;

      let roleIdString = "";

      if (user?.role && user?.role?.roleId !== "") {
        roleIdString = user.role.roleId;
        user.role.roleId = roleIdString?.split("$")[0];
      }

      const reqUser: Partial<User> = {
        ...user,
        email: undefined,
        userId: undefined,
      };

      set((state) => {
        state.addUser.loading = true;
      });

      return new Promise<UserTypes.CreateServiceResponse>((resolve, reject) => {
        BaseService.put(environment.api.users + "/" + userId, reqUser)
          .then((res) => {
            set(() => {
              user.email = encodeURI(email);
              if (user?.role && user?.role?.roleId !== "")
                user.role.roleName = roleIdString?.split("$")[1];
            });
            resolve(res.data);
          })
          .catch((error) => {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.addUser.loading = false;
            });
          });
      });
    },
  },
  fetchUser: {
    loading: false,
    fetched: false,
    error: "",
    actions: {},
    call: (userId) => {
      set((state) => {
        state.fetchUser.loading = true;
        state.fetchUser.fetched = false;
        state.fetchUser.error = "";
      });
      const res = BaseService.get(environment.api.users + "/" + userId)
        .then(function (res) {
          set((state) => {
            state.user = res.data.data.User;
            state.fetchUser.fetched = true;
            state.selectedUsers = [];
          });

          const data = res.data.data;
          if (data && data.actions) {
            set((state) => {
              state.fetchUser.actions = data.actions;
            });
          }
          return res;
        })
        .catch(function (error) {
          set((state) => {
            state.fetchUser.error =
              error.response?.data?.message || "user fetch error";
          });
          return error;
        })
        .finally(() => {
          set((state) => {
            state.fetchUser.loading = false;
            state.fetchUser.fetched = true;
          });
        });
      return res;
    },
  },
  deleteUser: {
    loading: false,
    call: (user, delegateUserId) => {
      const { selectedUsers } = get();
      const emails = [] as string[];
      const userId = user.userId;
      selectedUsers.map((item) => emails.push(item.email));
      set((state) => {
        state.deleteUser.loading = true;
      });

      const deleteRequest = delegateUserId
        ? BaseService.delete(environment.api.users, {
            data: { userId: userId, delegateUserId: delegateUserId },
          })
        : BaseService.delete(environment.api.users, {
            data: { userId: userId },
          });
      return new Promise<UserManagementServiceResponse>((resolve, reject) => {
        deleteRequest
          .then((res) => {
            resolve(res.data);
          })
          .catch((error) => {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.deleteUser.loading = false;
            });
          });
      });
    },
  },
  deactivateUsers: {
    loading: false,
    call: (user, delegateUserId) => {
      const { selectedUsers } = get();
      const emails = [] as string[];
      const userId = user.userId;
      selectedUsers.map((item) => emails.push(item.email));
      set((state) => {
        state.deactivateUsers.loading = true;
      });

      const deactivateRequest = delegateUserId
        ? BaseService.put(environment.api.users + "/" + userId, {
            status: "inactive",
            delegateUserId: delegateUserId,
          })
        : BaseService.put(environment.api.users + "/" + userId, {
            status: "inactive",
          });

      return new Promise<UserManagementServiceResponse>((resolve, reject) => {
        deactivateRequest
          .then((res) => {
            resolve(res.data);
          })
          .catch((error) => {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.deactivateUsers.loading = false;
            });
          });
      });
    },
  },
  reactivateUsers: {
    loading: false,
    call: (userId) => {
      const { selectedUsers } = get();
      const emails = [] as string[];
      selectedUsers.map((item) => emails.push(item.email));
      set((state) => {
        state.reactivateUsers.loading = true;
      });

      return new Promise<UserManagementServiceResponse>((resolve, reject) => {
        BaseService.put(environment.api.users + "/" + userId, {
          status: "active",
        })
          .then(function (res) {
            resolve(res.data);
          })
          .catch(function (error) {
            reject(error.response);
          })
          .finally(() => {
            set((state) => {
              state.reactivateUsers.loading = false;
            });
          });
      });
    },
  },
  addSelectedUser: (user: User) => {
    set((state) => {
      state.selectedUsers.push(user);
    });
  },
  addSelectAllUsers: (user: User[]) => {
    set((state) => {
      state.selectedUsers = user;
    });
  },
  deselectUser: (user: User) => {
    set((state) => {
      const index = state.selectedUsers.findIndex(
        (selectedUser) => selectedUser.email === user.email
      );
      state.selectedUsers.splice(index, 1);
    });
  },
  clearUsers: () => {
    set((state) => {
      state.users = [];
      state.paginationKey = "";
      state.getUsers.fetched = false;
      state.selectedUsers = [];
    });
  },
  clearAllSelectedUsers: () => {
    set({ selectedUsers: [] });
  },
  deactivateSelectedUser: () => {
    set({ selectedUsers: [] });
  },
  reactivateSelectedUser: () => {
    set({ selectedUsers: [] });
  },
  deleteSelectedUser: () => {
    set({ selectedUsers: [] });
  },
  getCountriesData: (countries: []) => {
    set((state) => {
      state.countiresDropdownList = countries;
    });
  },
});
